import React, {useCallback, useEffect, useRef, useState} from 'react';
import {DataTable} from "primereact/datatable";
import {Column} from "primereact/column";
import {OverlayPanel} from "primereact/overlaypanel";
import {Tag} from "primereact/tag";
import {InputText} from "primereact/inputtext";
import {InputNumber} from "primereact/inputnumber";
import {InputTextarea} from "primereact/inputtextarea";
import {InputSwitch} from "primereact/inputswitch";
import {ToggleButton} from "primereact/togglebutton";
import {Dropdown} from "primereact/dropdown";
import {Button} from "primereact/button";
import {Message} from "primereact/message";
import {Dialog} from "primereact/dialog";
import {TabView, TabPanel} from "primereact/tabview";

import {AppContext} from "../../HomePage";
import {CampaignContext} from "../../App";
import {urlValidator, getHostName, isAdmin, isCampaignManufacturer} from "../../utils/Utils";
import ScrapeStatus from "../Product/ScrapeStatus";
import {Loading} from "../../utils/InlineComponents";
import WebSearchSetting from "../Admin/WebSearchSetting";
import CompetitorCandidates from "../Campaign/CompetitorCandidates";
import ProbeLauncher from "../Campaign/ProbeLauncher";
import SiteDefinitionAdd from "../Admin/SiteDefinitionAdd";
import CopyToClipboard from "../../utils/CopyToClipboard";
import {CampaignService} from "../../service/CampaignService";
import {ScraperService} from "../../service/ScraperService";
import {CleanupService} from "../../service/CleanupService";
import {RequestProbeService} from "../../service/RequestProbeService";
import {normalizeHost, buildSiteBudgets, totalForHostIfKnown, isHostBudgeted, isBudgetValueSet} from "../Admin/JobSchedulingSiteSearchSetting";

const campaignService = new CampaignService();
const scraperService = new ScraperService();
const cleanupService = new CleanupService();
const requestProbeService = new RequestProbeService();

// Default discovery settings seeded if the campaign has none yet, so the
// existing competitor-suggestion trigger works without a new endpoint.
// Covers the keys both the new_competitor_suggestion and competitor_suggestion
// spiders read directly (selected_fields, page_number, exclude_products_*,
// and the run_competitor_suggestion_job params).
const DEFAULT_DISCOVERY_SETTINGS = {
    selected_fields: [{name: 'Name', code: 'name'}],   // search by product name (must match search_fields option)
    page_number: 3,
    google_shopping: false,              // Searching Website = Google (not Google Shopping)
    is_searching_enabled: true,
    is_site_searching_enabled: true,
    also_find_new_websites: true,
    exclude_products_with_suggestions: false,
    seller_filter: 'no_seller_filter',
    competitor_urls: [],
    // Reduce noise: exclude common social/junk hosts from discovery results.
    excluded_competitor_urls: [
        'https://facebook.com', 'https://instagram.com', 'https://youtube.com', 'https://pinterest.com',
        'https://wikipedia.org', 'https://reddit.com', 'https://twitter.com', 'https://github.com',
    ],
    job_units: 10,
    job_priority: 3,
    use_headless: false,
    divided_competitors_no: 0,
};

// Competitor DISCOVERY: finds which sites sell the campaign's products (Google
// search) and ranks them by how many products each site appeared for. Does NOT
// scrape products / touch the seller list (that is Phase 4 "Google Sellers").
const DISCOVERY_TYPE = 'competitor_discovery';

// A vertical action card: icon + title, description, then the action(s). Works
// well side-by-side in a responsive grid (unlike a wide horizontal strip).
const ActionCard = ({icon, color, title, hint, children}) => (
    <div style={{border: '1px solid #e5e7eb', borderRadius: 10, padding: 16, background: '#fff',
        display: 'flex', flexDirection: 'column', gap: 10}}>
        <div style={{display: 'flex', alignItems: 'center', gap: 10}}>
            <span style={{width: 36, height: 36, borderRadius: '50%', flexShrink: 0, display: 'inline-flex',
                alignItems: 'center', justifyContent: 'center', background: (color || '#2563eb') + '1a', color: color || '#2563eb'}}>
                <i className={icon} style={{fontSize: '1.05rem'}}/>
            </span>
            <div style={{fontWeight: 700}}>{title}</div>
        </div>
        {hint && <small style={{color: '#6b7280', flex: 1}}>{hint}</small>}
        <div style={{display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center'}}>{children}</div>
    </div>
);
const ACTION_GRID = {display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 14, marginBottom: 14};

// Self-contained multiplier cell: keeps its own draft so the row re-render
// from the parent never steals focus; commits on blur. An empty field means
// "inherit" -- the value that would be inherited is shown as the placeholder --
// so an explicitly typed 1 stays a real value that overrides the site.
const MultiplierInput = ({value, inherited, onCommit}) => {
    const [v, setV] = useState(value ?? null);
    // Enter has to reach the same commit the blur does: PrimeReact's InputNumber
    // ignores the key entirely, so a value typed and then left unblurred (Enter,
    // then Escape to close the dialog) was dropped without a word.
    return <span onKeyDown={(e) => e.key === 'Enter' && e.target.blur()}>
        <InputNumber value={v} inputStyle={{width: 70}} mode="decimal" minFractionDigits={1}
                     placeholder={inherited === null || inherited === undefined ? '—' : String(inherited)}
                     onValueChange={(e) => setV(e.value)} onBlur={() => onCommit(v)}/>
    </span>;
};

const stripTransientFlags = (c) => {
    const o = {...c};
    delete o.is_headless_browser_used;
    delete o.is_spider_ready;
    delete o.is_site_searching_ready;
    return o;
};

// Drill-down for a marketplace site: manage its individual sellers
// (multiplier + active for all campaigns; authorize/notify + contact for
// manufacturers). Each change persists immediately via updateCampaignCompetitor
// (+ the dedicated cleanup endpoints for multiplier / activation).
export const SellersDialog = ({t, competitor, campaignId, isManufacturer, autoActiveDefault, toast, onError, onClose}) => {
    // localeCompare, not raw ">": a marketplace seller list is full of Turkish
    // characters, and codepoint order scatters them ("Çınar" after "Zeki").
    const sortByName = (arr) => [...arr].sort((a, b) =>
        (a.seller_name || '').localeCompare(b.seller_name || '', 'tr', {sensitivity: 'base'}));
    const [comp, setComp] = useState(() => ({...competitor, sellers: sortByName(competitor.sellers || [])}));
    const [editSeller, setEditSeller] = useState(null);
    const [saving, setSaving] = useState(false);
    const [search, setSearch] = useState('');
    // "which sellers did I give a multiplier?" over a list of hundreds
    const [multiplierFilter, setMultiplierFilter] = useState('all');   // all | set | unset
    const [activeFilter, setActiveFilter] = useState('all');           // all | active | passive
    const [first, setFirst] = useState(0);   // paginator offset, reset on search
    const sellerActive = (s) => s.is_active ?? autoActiveDefault;
    const ok = () => toast.current.show({severity: 'success', summary: t('campaign.competitors.update', 'Güncellendi'), detail: t('message_detail.successful')});
    const withSeller = (ns) => comp.sellers.map(x => x.seller_name === ns.seller_name ? ns : x);
    const persist = (updatedSellers) => {
        const toSave = stripTransientFlags({...comp, sellers: updatedSellers});
        return campaignService.updateCampaignCompetitor(campaignId, toSave)
            .then(() => setComp(c => ({...c, sellers: updatedSellers})));
    };

    const commitMultiplier = (seller, multiplier) => {
        if ((seller.multiplier ?? null) === (multiplier ?? null)) return;
        persist(withSeller({...seller, multiplier})).then(ok).catch(onError);
    };
    const toggleActive = (seller) => {
        const next = !sellerActive(seller);
        const ns = {...seller, is_active: next};
        cleanupService.activateOrDeactivateCompetitors(campaignId, seller.seller_name, next)
            .then(() => persist(withSeller(ns))).then(ok).catch(onError);
    };
    const onEditField = (key, value) => setEditSeller(s => ({...s, [key]: value}));
    const saveSellerEdit = () => {
        setSaving(true);
        persist(withSeller(editSeller))
            .then(() => { setEditSeller(null); ok(); })
            .catch(onError).finally(() => setSaving(false));
    };

    // Filtering only narrows what's rendered -- every edit still persists against
    // comp.sellers, so a search can't drop sellers from the saved list.
    const query = search.trim().toLocaleLowerCase('tr');
    const hasOwnMultiplier = (s) => s.multiplier !== null && s.multiplier !== undefined;
    const visibleSellers = comp.sellers.filter(s =>
        (!query || (s.seller_name || '').toLocaleLowerCase('tr').includes(query)) &&
        (multiplierFilter === 'all' || (multiplierFilter === 'set') === hasOwnMultiplier(s)) &&
        // sellerActive, not is_active: a seller nobody has switched either way
        // follows the campaign's auto-activation default and has to filter as
        // whatever it actually behaves like.
        (activeFilter === 'all' || (activeFilter === 'active') === sellerActive(s)));

    return (
        <Dialog header={competitor.url} visible style={{width: '720px', maxWidth: '95vw'}} modal
                onHide={() => onClose()}>
            {comp.sellers.length > 10 &&
                <div style={{display: 'flex', justifyContent: 'flex-end', gap: 8, marginBottom: 10, flexWrap: 'wrap'}}>
                    <Dropdown value={multiplierFilter} style={{width: '13em'}}
                              options={[{label: t('onboarding.sites.multiplierAll', 'Tüm satıcılar'), value: 'all'},
                                        {label: t('onboarding.sites.multiplierSet', 'Çarpanı olanlar'), value: 'set'},
                                        {label: t('onboarding.sites.multiplierUnset', 'Çarpanı olmayanlar'), value: 'unset'}]}
                              onChange={(e) => { setMultiplierFilter(e.value); setFirst(0); }}/>
                    <Dropdown value={activeFilter} style={{width: '10em'}}
                              options={[{label: t('onboarding.sites.stateAll', 'Aktif ve pasif'), value: 'all'},
                                        {label: t('onboarding.sites.stateActive', 'Aktifler'), value: 'active'},
                                        {label: t('onboarding.sites.statePassive', 'Pasifler'), value: 'passive'}]}
                              onChange={(e) => { setActiveFilter(e.value); setFirst(0); }}/>
                    <span className="p-input-icon-left" style={{width: '16em'}}>
                        <i className="pi pi-search"/>
                        <InputText value={search} style={{width: '100%'}}
                                   placeholder={t('onboarding.sites.sellerSearch', 'Search seller')}
                                   onChange={(e) => { setSearch(e.target.value); setFirst(0); }}/>
                    </span>
                </div>}
            {/* first is clamped: clearing the last multiplier on a page can shrink a
                filtered list past the paginator offset, and the table has to land
                on page one rather than on an empty page. */}
            <DataTable value={visibleSellers} dataKey="seller_name" responsiveLayout="scroll"
                       sortField="seller_name" sortOrder={1}
                       paginator={visibleSellers.length > 10} rows={10}
                       first={first < visibleSellers.length ? first : 0}
                       onPage={(e) => setFirst(e.first)}
                       emptyMessage={t('onboarding.sites.noSellerMatch', 'No seller matches the search.')}>
                <Column field="seller_name" header={t('onboarding.sites.seller', 'Satıcı')} sortable/>
                <Column header={t('product_detail.multiplier', 'Çarpan')} style={{width: '9em'}}
                        field="multiplier" sortable
                        body={(s) => <MultiplierInput value={s.multiplier} inherited={comp.multiplier}
                                                      onCommit={(v) => commitMultiplier(s, v)}/>}/>
                <Column header={t('onboarding.sites.active', 'Aktif')} style={{width: '12em', textAlign: 'center'}}
                        body={(s) =>
                            <span style={{display: 'flex', gap: 8, alignItems: 'center', justifyContent: 'center'}}>
                                <InputSwitch checked={sellerActive(s)} onChange={() => toggleActive(s)}
                                             tooltipOptions={{position: 'bottom'}}
                                             tooltip={sellerActive(s) ? t('actions.deactivate', 'Pasifleştir') : t('actions.activate', 'Aktifleştir')}/>
                                {isManufacturer &&
                                    <Button icon="pi pi-pencil" className="p-button-text p-button-sm"
                                            tooltip={t('actions.edit', 'Düzenle')} tooltipOptions={{position: 'bottom'}}
                                            onClick={() => setEditSeller({...s})}/>}
                            </span>}/>
            </DataTable>

            {/* manufacturer-only seller edit: authorize/notify + contact */}
            <Dialog header={editSeller?.seller_name} visible={!!editSeller} style={{width: '520px', maxWidth: '95vw'}} modal
                    onHide={() => !saving && setEditSeller(null)}
                    footer={editSeller &&
                        <div>
                            <Button label={t('actions.cancel', 'İptal')} className="p-button-text" disabled={saving}
                                    onClick={() => setEditSeller(null)}/>
                            <Button label={t('actions.save', 'Kaydet')} icon="pi pi-save" loading={saving} onClick={saveSellerEdit}/>
                        </div>}>
                {editSeller && (
                    <div className="p-fluid" style={{display: 'flex', flexDirection: 'column', gap: 18, marginTop: 6}}>
                        <div style={{display: 'flex', gap: 12, flexWrap: 'wrap'}}>
                            <ToggleButton onLabel={t('campaign.competitors.authorized', 'Yetkili')} offLabel={t('campaign.competitors.not_authorized', 'Yetkisiz')}
                                          onIcon="pi pi-check" offIcon="pi pi-times" checked={!!editSeller.is_authorized}
                                          onChange={(e) => onEditField('is_authorized', e.value)}/>
                            <ToggleButton onLabel={t('campaign.competitors.notified', 'Bildirildi')} offLabel={t('campaign.competitors.not_notified', 'Bildirilmedi')}
                                          onIcon="pi pi-check" offIcon="pi pi-times" checked={!!editSeller.is_notified}
                                          onChange={(e) => onEditField('is_notified', e.value)}/>
                        </div>
                        <span className="p-float-label">
                            <InputText id="s_email" value={editSeller.email || ''} tooltip={t('actions.multiple_email_format')}
                                       onChange={(e) => onEditField('email', e.target.value)}/>
                            <label htmlFor="s_email">{t('campaign.competitors.email', 'E-posta')}</label>
                        </span>
                        <span className="p-float-label">
                            <InputText id="s_phone" value={editSeller.phone || ''} onChange={(e) => onEditField('phone', e.target.value)}/>
                            <label htmlFor="s_phone">{t('campaign.competitors.phone', 'Telefon')}</label>
                        </span>
                        <span className="p-float-label">
                            <InputText id="s_address" value={editSeller.address || ''} onChange={(e) => onEditField('address', e.target.value)}/>
                            <label htmlFor="s_address">{t('campaign.competitors.address', 'Adres')}</label>
                        </span>
                        <span className="p-float-label">
                            <InputTextarea id="s_note" rows={4} value={editSeller.note || ''} onChange={(e) => onEditField('note', e.target.value)}/>
                            <label htmlFor="s_note">{t('campaign.competitors.note', 'Not')}</label>
                        </span>
                    </div>
                )}
            </Dialog>
        </Dialog>
    );
};

// ---- host-scoped admin operations (scrape jobs + critical cleanup) --------
// Scrape jobs target the host; cleanups take the full competitor URL (matches
// the legacy My Sellers' URLs behaviour).
const HOST_SCRAPE_OPS = [
    {key: 'hostPrices', icon: 'pi pi-dollar',
        build: (cid, host) => ({campaign_id: cid, urls: [host], type: 'prices_host_based_update_run'})},
    {key: 'updateSuggestions', icon: 'pi pi-sync',
        build: (cid, host) => ({campaign_id: cid, urls: [host], type: 'suggestions_update'})},
    {key: 'reviews', icon: 'pi pi-comments',
        build: (cid, host) => ({spider_name: 'products_update_review', fields: ['reviews'], campaign_id: cid, urls: [host], type: 'products_update_partial'})},
    {key: 'failedReviews', icon: 'pi pi-comment',
        build: (cid, host) => ({spider_name: 'products_update_failed_review', fields: ['reviews'], campaign_id: cid, urls: [host], type: 'products_update_partial'})},
];
// Only for campaigns running the supplier module: every other campaign has no
// supplier rows for this host, so the job would start and find nothing to do.
// The spider narrows itself to `urls`; a request without them is the scheduled
// campaign-wide refresh, which is a many-hour job and not what this button is.
const HOST_SUPPLIER_OP = {key: 'supplierPrices', icon: 'pi pi-truck',
    build: (cid, host) => ({campaign_id: cid, urls: [host], type: 'suppliers_campaign_update'})};
const HOST_CLEANUP_OPS = [
    {key: 'removeCompetitors', icon: 'pi pi-trash', run: (id, url) => cleanupService.removeCompetitors(id, url)},
    {key: 'removeProductsByOwnerHost', icon: 'pi pi-box', run: (id, url) => cleanupService.removeProductsByOwnerHost(id, url)},
    {key: 'removeDeactivatedCompetitors', icon: 'pi pi-ban', run: (id, url) => cleanupService.removeDeactivatedCompetitors(id, url)},
    {key: 'clearSuggestionsOfCompetitor', icon: 'pi pi-google', run: (id, url) => cleanupService.clearSuggestionsOfCompetitor(id, url)},
    {key: 'activateCompetitorUrls', icon: 'pi pi-check-circle', run: (id, url) => cleanupService.activateCompetitorUrls(id, url)},
    {key: 'clearManuelShippingPrices', icon: 'pi pi-shopping-cart', run: (id, url) => cleanupService.clearManuelShippingPrices(id, url)},
];

const HostOpsCard = ({t, icon, opKey, danger, loading, onRun}) => (
    <div style={{border: '1px solid #e5e7eb', borderRadius: 10, padding: 14, background: '#fff',
        display: 'flex', flexDirection: 'column', gap: 8}}>
        <div style={{fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8}}>
            <i className={icon} style={{color: danger ? '#dc2626' : '#2563eb'}}/>
            {t('onboarding.sites.hostOps.' + opKey + '.title')}
        </div>
        <small style={{color: '#6b7280', flex: 1}}>{t('onboarding.sites.hostOps.' + opKey + '.desc')}</small>
        <Button label={t('onboarding.sites.hostOps.run', 'Çalıştır')} icon={icon}
                className={danger ? 'p-button-outlined p-button-danger' : 'p-button-outlined'}
                loading={loading} onClick={onRun}/>
    </div>
);

// Host operations dialog: scrape jobs + (confirmed) critical cleanups, scoped
// to the marketplace/host of one monitored site.
const HostOpsDialog = ({t, row, campaignId, supplierModuleActive, toast, onError, onClose}) => {
    const host = getHostName(row.url);
    const scrapeOps = supplierModuleActive ? [...HOST_SCRAPE_OPS, HOST_SUPPLIER_OP] : HOST_SCRAPE_OPS;
    const [busy, setBusy] = useState(null);
    const [confirm, setConfirm] = useState(null);
    const ok = (opKey) => toast.current.show({severity: 'success', summary: t('onboarding.sites.hostOps.' + opKey + '.title'), detail: t('message_detail.successful')});

    const runScrape = (op) => {
        setBusy(op.key);
        scraperService.saveScrapingRequest(op.build(campaignId, host))
            .then(() => ok(op.key)).catch(onError).finally(() => setBusy(null));
    };
    const runCleanup = () => {
        const op = confirm;
        setBusy(op.key);
        op.run(campaignId, row.url)
            .then(() => { ok(op.key); setConfirm(null); }).catch(onError).finally(() => setBusy(null));
    };

    const grid = {display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 14};
    return (
        <Dialog header={`${t('onboarding.sites.hostOps.title', 'Host işlemleri')} · ${host}`} visible
                style={{width: '820px', maxWidth: '95vw'}} modal onHide={() => !busy && onClose()}>
            <div style={{fontWeight: 700, margin: '4px 0 10px'}}>{t('onboarding.sites.hostOps.scrapeGroup', 'Tarama')}</div>
            <div style={grid}>
                {scrapeOps.map(op =>
                    <HostOpsCard key={op.key} t={t} icon={op.icon} opKey={op.key}
                                 loading={busy === op.key} onRun={() => runScrape(op)}/>)}
            </div>
            <div style={{fontWeight: 700, margin: '20px 0 10px'}}>{t('onboarding.sites.hostOps.cleanupGroup', 'Kritik temizlik')}</div>
            <div style={grid}>
                {HOST_CLEANUP_OPS.map(op =>
                    <HostOpsCard key={op.key} t={t} icon={op.icon} opKey={op.key} danger
                                 loading={busy === op.key} onRun={() => setConfirm(op)}/>)}
            </div>

            <Dialog header={confirm ? t('onboarding.sites.hostOps.' + confirm.key + '.title') : ''} visible={!!confirm}
                    style={{width: '440px', maxWidth: '95vw'}} modal onHide={() => !busy && setConfirm(null)}
                    footer={confirm &&
                        <div>
                            <Button label={t('actions.cancel', 'İptal')} className="p-button-text" disabled={!!busy} onClick={() => setConfirm(null)}/>
                            <Button label={t('onboarding.sites.hostOps.run', 'Çalıştır')} icon="pi pi-check" className="p-button-danger"
                                    loading={!!busy} onClick={runCleanup}/>
                        </div>}>
                {confirm && <div>
                    <p style={{marginTop: 0}}>{t('onboarding.sites.hostOps.' + confirm.key + '.desc')}</p>
                    <Message severity="warn" text={t('onboarding.sites.hostOps.irreversible', 'Bu işlem geri alınamaz.')} style={{display: 'block'}}/>
                </div>}
            </Dialog>
        </Dialog>
    );
};

const SitesOnboardingStep = ({t, status, refresh, onOpenSiteDefinition, wizardMode}) => {
    const {toast, setError} = React.useContext(AppContext);
    const {campaigns, selectedCampaign, loadCampaigns} = React.useContext(CampaignContext);
    const campaignId = selectedCampaign?.id;

    // Sihirbaz içinde admin de son-kullanıcı arayüzünü görür (wizardMode=true).
    const admin = wizardMode ? false : isAdmin();

    const [sites, setSites] = useState([]);
    const [probeWebsites, setProbeWebsites] = useState(new Set());
    const [url, setUrl] = useState('');
    const [urlErr, setUrlErr] = useState('');
    const [loading, setLoading] = useState(false);
    const [loadingSites, setLoadingSites] = useState(true);
    const [discovering, setDiscovering] = useState(false);
    const [scrapingRequest, setScrapingRequest] = useState(null);
    const [showDiscoverDialog, setShowDiscoverDialog] = useState(false);
    const [newSellers, setNewSellers] = useState([]);
    const [loadingNewSellers, setLoadingNewSellers] = useState(true);
    const [finding, setFinding] = useState(false);
    const [showNewSellersDialog, setShowNewSellersDialog] = useState(false);
    const [showBaseSpiderDialog, setShowBaseSpiderDialog] = useState(false);
    const [findingSpiders, setFindingSpiders] = useState(false);
    const [editComp, setEditComp] = useState(null);
    const [budgetDraft, setBudgetDraft] = useState({});   // {search, refresh} draft for editComp's host, admin-only
    const [savingComp, setSavingComp] = useState(false);
    const [sellersComp, setSellersComp] = useState(null);
    const rowMenu = useRef(null);
    const [menuRow, setMenuRow] = useState(null);
    const [siteDef, setSiteDef] = useState(null);   // selectedDefinition for SiteDefinitionAdd (null = closed)
    const [hostOpsRow, setHostOpsRow] = useState(null);   // host operations dialog target
    const [showCopyDialog, setShowCopyDialog] = useState(false);
    const [copyTo, setCopyTo] = useState(null);
    const [copying, setCopying] = useState(false);
    const [candidates, setCandidates] = useState([]);   // discovered competitor candidates (for copy)
    const [nsFilter, setNsFilter] = useState('all');    // New Sellers spider-readiness filter
    const [nsSearch, setNsSearch] = useState('');       // New Sellers free-text search
    const [nsFirst, setNsFirst] = useState(0);          // New Sellers paginator offset
    const [msFilter, setMsFilter] = useState('all');    // Monitored sites status filter
    const [msSearch, setMsSearch] = useState('');       // Monitored sites free-text search
    const [msFirst, setMsFirst] = useState(0);          // Monitored sites paginator offset
    const isManufacturer = isCampaignManufacturer(selectedCampaign);
    // Daily request budget: {"<normalized host>": {search, refresh}}. Two rows
    // in the monitored list can normalize to the same host and therefore share
    // one entry (see normalizeHost) -- edit dialog and row tag both read this.
    const siteBudgets = selectedCampaign?.settings?.site_budgets || {};

    const monitoredUrlsText = sites.map(s => s.url).join('\n');
    const candidateUrlsText = candidates.map(c => 'https://' + c.host).join('\n');

    // Alphabetical by host (protocol and "www." ignored) so the order does not
    // depend on how each URL happened to be typed in. Shared by both URL lists.
    const siteSortKey = (row) => (getHostName(row.url) || row.url || '').toLowerCase();
    const byHost = (a, b) => siteSortKey(a).localeCompare(siteSortKey(b));

    // New Sellers: filter by spider readiness (admin column) + free-text search
    const isScrapable = (r) => !!(r.is_spider_ready || r.spider_name);
    const nsQuery = nsSearch.trim().toLowerCase();
    const readinessFilteredNewSellers = nsFilter === 'scrapable' ? newSellers.filter(isScrapable)
        : nsFilter === 'needsSpider' ? newSellers.filter(r => !isScrapable(r))
        : newSellers;
    const filteredNewSellers = (nsQuery
        ? readinessFilteredNewSellers.filter(r => (r.url || '').toLowerCase().includes(nsQuery))
        : [...readinessFilteredNewSellers])
        .sort(byHost);

    // Copy this campaign's monitored competitor URLs into another campaign.
    const copyToCampaign = () => {
        if (!copyTo) return;
        setCopying(true);
        campaignService.copyCampaignCompetitors(campaignId, copyTo)
            .then(() => {
                setShowCopyDialog(false);
                setCopyTo(null);
                toast.current.show({severity: 'success', summary: t('onboarding.sites.copyToCampaign', 'Başka kampanyaya kopyala'), detail: t('message_detail.successful')});
            })
            .catch(fail).finally(() => setCopying(false));
    };

    // Open the Site Definition modal for a host: edit if a definition exists,
    // otherwise the add form pre-filled with the host (and any base spider).
    const openSiteDefinition = (row) => {
        const host = getHostName(row.url);
        if (rowMenu.current) rowMenu.current.hide();
        scraperService.getSiteDefinitionByUrl(host)
            .then(def => setSiteDef(def && def.id ? def : {url: host, spider_name: row.used_base_spider || ''}))
            .catch(() => setSiteDef({url: host, spider_name: row.used_base_spider || ''}));
    };
    const autoActiveDefault = !!selectedCampaign?.new_seller_automatically_activated;

    const findBaseSpiders = () => {
        setFindingSpiders(true);
        scraperService.saveScrapingRequest({type: 'spider_finder', campaign_id: campaignId})
            .then(() => {
                setShowBaseSpiderDialog(false);
                toast.current.show({
                    severity: 'success',
                    summary: t('onboarding.sites.findBaseSpiders', 'Base Spider Bul'),
                    detail: t('message_detail.successful'),
                });
            })
            .catch(fail).finally(() => setFindingSpiders(false));
    };

    // Live-edited discovery settings (WebSearchSetting mutates this object in
    // place). Seeded from the campaign, with searching enabled by default.
    const [discoverySettings] = useState(() => {
        const cs = (selectedCampaign?.settings && selectedCampaign.settings.competitor_suggestion) || {};
        return {
            ...DEFAULT_DISCOVERY_SETTINGS,
            ...cs,
            is_searching_enabled: cs.is_searching_enabled !== undefined ? cs.is_searching_enabled : true,
            selected_fields: (cs.selected_fields && cs.selected_fields.length) ? cs.selected_fields : DEFAULT_DISCOVERY_SETTINGS.selected_fields,
            page_number: cs.page_number || DEFAULT_DISCOVERY_SETTINGS.page_number,
        };
    });

    const loadNewSellers = useCallback(() => {
        if (!campaignId) return;
        setLoadingNewSellers(true);
        campaignService.getCampaignCompetitorSuggestions(campaignId)
            .then(c => setNewSellers((c && c.competitor_suggestions) || [])).catch(() => {})
            .finally(() => setLoadingNewSellers(false));
    }, [campaignId]);

    const reload = useCallback(() => {
        if (!campaignId) return;
        setLoadingSites(true);
        campaignService.getCampaignCompetitors(campaignId)
            .then(setSites).catch(() => {}).finally(() => setLoadingSites(false));
        // websites that already have a request probe (to flag rows + enable "open")
        requestProbeService.listResults({limit: 200})
            .then(results => setProbeWebsites(new Set((results || []).map(r => (r.website || '').replace('www.', '')))))
            .catch(() => {});
        loadNewSellers();
    }, [campaignId, loadNewSellers]);

    useEffect(() => {
        reload();
    }, [reload]);

    const after = () => {
        reload();
        if (refresh) refresh();
    };
    const fail = (error) => {
        setError(error);
        setError(null);
        setLoading(false);
        setDiscovering(false);
    };

    // readiness badge from the backend-annotated flags
    const readiness = (row) => {
        if (row.is_site_searching_ready)
            return {label: t('onboarding.sites.ready', 'arama-hazır'), severity: 'success'};
        if (row.is_spider_ready)
            return {label: t('onboarding.sites.needsSearch', 'site-içi arama yok'), severity: 'warning'};
        return {label: t('onboarding.sites.needsSpider', 'spider gerekli'), severity: 'danger'};
    };

    // ---- (a) add own site ----------------------------------------------------
    // Several sites can be pasted at once, separated by "||" (same convention as
    // the campaign competitor screen and the products step).
    const addSite = () => {
        const urls = url.split('||').map(u => u.trim()).filter(Boolean);
        if (!urls.length) {
            setUrlErr(t('validations.invalid_value'));
            return;
        }
        const err = urls.map(u => urlValidator(u, t)).find(Boolean);
        if (err) {
            setUrlErr(err);
            return;
        }
        setUrlErr('');

        // skip URLs already monitored, and duplicates inside the pasted list
        const seen = new Set(sites.map(s => (s.url || '').toLowerCase()));
        const fresh = [];
        urls.forEach(u => {
            if (seen.has(u.toLowerCase())) return;
            seen.add(u.toLowerCase());
            fresh.push(u);
        });
        if (fresh.length !== urls.length)
            toast.current.show({
                severity: 'warn',
                summary: t('campaign.competitors.duplicate'),
                detail: t('campaign.competitors.duplicate_detail'),
            });
        if (!fresh.length) return;

        setLoading(true);
        campaignService.addCampaignCompetitor(campaignId, fresh.map(u => ({url: u})))
            .then(() => {
                setUrl('');
                toast.current.show({severity: 'success', summary: t('onboarding.sites.added', 'Site eklendi')});
                setLoading(false);
                after();
            }).catch(fail);
    };

    const removeSite = (row) => {
        campaignService.deleteCampaignCompetitor(campaignId, {url: row.url})
            .then(after).catch(fail);
    };

    // ---- per-site edit: drop backend-only flags before saving --------------
    const onEditField = (key, value) => setEditComp(c => ({...c, [key]: value}));
    // Cross-campaign total for editComp's host, suppressed (null) rather than
    // guessed if editComp's campaign is not the one CampaignContext.campaigns
    // is scoped to -- see totalForHostIfKnown's own doc for why.
    const budgetTotalNote = (kind) => {
        const total = totalForHostIfKnown(campaigns, selectedCampaign, editComp?.url, kind);
        return total === null
            ? t('suggestion.site_budget_total_unavailable')
            : t('suggestion.site_budget_total', {total});
    };
    // The daily request budget lives on campaign.settings, not on the
    // campaign_competitor document -- it must go through saveCampaignSettings
    // (same call shape as persistSettings above), merging site_budgets into
    // the existing settings rather than replacing them. Admin-only, so this
    // is skipped entirely when the dialog never showed the budget section.
    const saveCompetitor = () => {
        setSavingComp(true);
        const tasks = [campaignService.updateCampaignCompetitor(campaignId, stripTransientFlags(editComp))];

        if (isAdmin()) {
            const mergedBudgets = buildSiteBudgets(selectedCampaign.settings?.site_budgets, editComp.url, budgetDraft);
            const merged = {...(selectedCampaign.settings || {}), site_budgets: mergedBudgets};
            tasks.push(
                campaignService.saveCampaignSettings(campaignId, selectedCampaign.use_sale_value, merged)
                    .then(() => {
                        selectedCampaign.settings = merged;
                        if (loadCampaigns) loadCampaigns();
                    })
            );
        }

        Promise.all(tasks)
            .then(() => {
                setEditComp(null);
                setBudgetDraft({});
                toast.current.show({severity: 'success', summary: t('campaign.competitors.update', 'Güncellendi'), detail: t('message_detail.successful')});
                after();
            })
            .catch(fail).finally(() => setSavingComp(false));
    };
    // is_active default mirrors legacy: respects campaign auto-activation flag
    const siteActive = (row) => row.is_active ?? !!selectedCampaign.new_seller_automatically_activated;
    const toggleActive = (row) => {
        const next = !siteActive(row);
        const comp = stripTransientFlags({...row, is_active: next});
        if (comp.sellers) comp.sellers.forEach(s => { s.is_active = next; });
        cleanupService.activateOrDeactivateCampaignCompetitor(campaignId, row.url, next)
            .then(() => campaignService.updateCampaignCompetitor(campaignId, comp))
            .then(() => {
                toast.current.show({severity: 'success', summary: t('campaign.competitors.update', 'Güncellendi'), detail: t('message_detail.successful')});
                after();
            })
            .catch(fail);
    };

    // ---- New Sellers (competitor_suggestions): add to monitored / delete -----
    const addSuggestionToSites = (row) => {
        Promise.all([
            // carry the suggestion's base spider so Monitored shows which spider it works with
            campaignService.addCampaignCompetitor(campaignId, [{url: row.url, used_base_spider: row.spider_name || null}]),
            campaignService.deleteCampaignCompetitorSuggestion(campaignId, row),
        ]).then(() => {
            toast.current.show({severity: 'success', summary: t('onboarding.sites.added', 'Site eklendi')});
            after();
        }).catch(fail);
    };
    const deleteSuggestion = (row) => {
        campaignService.deleteCampaignCompetitorSuggestion(campaignId, row)
            .then(loadNewSellers).catch(fail);
    };
    // Popup "Run" -> persist the (live-edited) settings + create the job.
    const runFindNewSellers = () => {
        setFinding(true);
        persistSettings({competitor_urls: sites.map(s => s.url)})
            .then(() => scraperService.saveScrapingRequest({type: 'new_competitor_suggestion', campaign_id: campaignId}))
            .then(() => {
                setShowNewSellersDialog(false);
                toast.current.show({
                    severity: 'success',
                    summary: t('onboarding.sites.findNewSellers', 'Yeni Satıcı Bul'),
                    detail: t('message_detail.successful'),
                });
            })
            .catch(fail).finally(() => setFinding(false));
    };

    // Persist the (live-edited) discovery settings into campaign.settings and
    // refresh the campaign context so other screens reflect the change.
    const persistSettings = (extra) => {
        const cs = {...discoverySettings, ...(extra || {})};
        const merged = {...(selectedCampaign.settings || {}), competitor_suggestion: cs};
        return campaignService.saveCampaignSettings(campaignId, selectedCampaign.use_sale_value, merged)
            .then(() => {
                selectedCampaign.settings = merged;
                if (loadCampaigns) loadCampaigns();
            });
    };

    // ---- (b) discovery: from the popup "Run" -> persist settings + trigger ---
    const runDiscovery = () => {
        setDiscovering(true);
        const extra = {competitor_urls: sites.map(s => s.url)};
        persistSettings(extra)
            .then(() => scraperService.saveScrapingRequest({type: DISCOVERY_TYPE, url: '', campaign_id: campaignId}))
            .then(() => {
                setShowDiscoverDialog(false);
                toast.current.show({
                    severity: 'success',
                    summary: t('onboarding.sites.discoverStarted', 'Keşif başlatıldı'),
                    detail: t('onboarding.sites.discoverHint', 'Rakip siteler birazdan listelenecek.'),
                });
                return scraperService.getLastScrapingRequest(campaignId, DISCOVERY_TYPE)
                    .then(reqs => setScrapingRequest(reqs && reqs[0])).catch(() => {});
            })
            .then(() => setDiscovering(false))
            .catch(fail);
    };

    const sitesSummary = (status?.steps || []).find(s => s.key === 'sites')?.summary;
    const selected = sitesSummary?.selected ?? sites.length;
    const readyCount = sitesSummary?.ready ?? sites.filter(s => s.is_site_searching_ready).length;

    // Monitored sites: filter by readiness/active status
    const siteStatus = (row) => row.is_site_searching_ready ? 'ready' : row.is_spider_ready ? 'needsSearch' : 'needsSpider';
    const msQuery = msSearch.trim().toLowerCase();
    const statusFilteredSites = msFilter === 'all' ? sites
        : msFilter === 'inactive' ? sites.filter(r => !siteActive(r))
        : msFilter === 'hasBaseSpider' ? sites.filter(r => !!r.used_base_spider)
        : sites.filter(r => siteStatus(r) === msFilter);
    const filteredSites = (msQuery
        ? statusFilteredSites.filter(r => (r.url || '').toLowerCase().includes(msQuery))
        : [...statusFilteredSites])
        .sort(byHost);

    return (
        <div>
            {/* heading + live count */}
            <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14, gap: 12, flexWrap: 'wrap'}}>
                <h2 style={{margin: 0, display: 'flex', alignItems: 'center', gap: 10}}>
                    <i className="pi pi-globe"/> {t('onboarding.step.sites.label', 'Siteler')}
                </h2>
                {/* "X seçildi · Y arama-hazır" — kampanya ekleme sihirbazında gizli. */}
                {!wizardMode &&
                <Tag icon="pi pi-check-circle" severity={selected > 0 ? 'success' : undefined}
                     value={t('onboarding.sites.count', `${selected} seçildi · ${readyCount} arama-hazır`)
                         .replace('{n}', selected).replace('{ready}', readyCount)}/>}
            </div>

            {/* settings popup: configure -> Save (persist) or Run (persist + start job) */}
            <Dialog header={t('onboarding.sites.discoverySettings', 'Keşif ayarları')} visible={showDiscoverDialog}
                    style={{width: '680px', maxWidth: '95vw'}} modal onHide={() => setShowDiscoverDialog(false)}
                    footer={
                        <div>
                            <Button label={t('actions.cancel', 'İptal')} className="p-button-text"
                                    onClick={() => setShowDiscoverDialog(false)}/>
                            <Button label={t('onboarding.sites.run', 'Çalıştır')} icon="pi pi-play"
                                    onClick={runDiscovery} disabled={discovering}/>
                        </div>
                    }>
                <WebSearchSetting webSearchSetting={discoverySettings}/>
            </Dialog>

            {/* find base spiders popup: explain + Run */}
            <Dialog header={t('onboarding.sites.findBaseSpiders', 'Base Spider Bul')} visible={showBaseSpiderDialog}
                    style={{width: '480px', maxWidth: '95vw'}} modal onHide={() => !findingSpiders && setShowBaseSpiderDialog(false)}
                    footer={
                        <div>
                            <Button label={t('actions.cancel', 'İptal')} className="p-button-text" disabled={findingSpiders}
                                    onClick={() => setShowBaseSpiderDialog(false)}/>
                            <Button label={t('onboarding.sites.run', 'Çalıştır')} icon="pi pi-play"
                                    loading={findingSpiders} disabled={findingSpiders} onClick={findBaseSpiders}/>
                        </div>
                    }>
                <span>{t('onboarding.sites.findBaseSpidersHint')}</span>
            </Dialog>

            {/* find new sellers popup: configure search settings -> Run */}
            <Dialog header={t('onboarding.sites.findNewSellers', 'Yeni Satıcı Bul')} visible={showNewSellersDialog}
                    style={{width: '680px', maxWidth: '95vw'}} modal onHide={() => !finding && setShowNewSellersDialog(false)}
                    footer={
                        <div>
                            <Button label={t('actions.cancel', 'İptal')} className="p-button-text" disabled={finding}
                                    onClick={() => setShowNewSellersDialog(false)}/>
                            <Button label={t('onboarding.sites.run', 'Çalıştır')} icon="pi pi-play"
                                    loading={finding} disabled={finding} onClick={runFindNewSellers}/>
                        </div>
                    }>
                <WebSearchSetting webSearchSetting={discoverySettings}/>
            </Dialog>

            {/* per-site edit: shipping (all) + violation/contact (manufacturer) */}
            <Dialog header={editComp?.url} visible={!!editComp} style={{width: '560px', maxWidth: '95vw'}} modal
                    onHide={() => { if (!savingComp) { setEditComp(null); setBudgetDraft({}); } }}
                    footer={editComp &&
                        <div>
                            <Button label={t('actions.cancel', 'İptal')} className="p-button-text" disabled={savingComp}
                                    onClick={() => { setEditComp(null); setBudgetDraft({}); }}/>
                            <Button label={t('actions.save', 'Kaydet')} icon="pi pi-save" loading={savingComp} onClick={saveCompetitor}/>
                        </div>}>
                {editComp && (
                    <div className="p-fluid" style={{display: 'flex', flexDirection: 'column', gap: 18, marginTop: 6}}>
                        <div>
                            <div style={{fontWeight: 700, marginBottom: 12}}>{t('onboarding.sites.shippingSection', 'Kargo / Fiyat')}</div>
                            <div style={{display: 'flex', gap: 16, flexWrap: 'wrap'}}>
                                <span className="p-float-label" style={{flex: 1, minWidth: 200}}>
                                    <InputNumber id="shipping_price" value={editComp.shipping_price} mode="decimal" minFractionDigits={2}
                                                 onValueChange={(e) => onEditField('shipping_price', e.value)}/>
                                    <label htmlFor="shipping_price">{t('product_detail.shipping_price', 'Kargo ücreti')}</label>
                                </span>
                                <span className="p-float-label" style={{flex: 1, minWidth: 200}}>
                                    <InputNumber id="shipping_price_threshold" value={editComp.shipping_price_threshold} mode="decimal" minFractionDigits={2}
                                                 onValueChange={(e) => onEditField('shipping_price_threshold', e.value)}/>
                                    <label htmlFor="shipping_price_threshold">{t('product_detail.shipping_price_threshold', 'Ücretsiz kargo eşiği')}</label>
                                </span>
                                <span className="p-float-label" style={{flex: 1, minWidth: 200}}>
                                    <InputNumber id="multiplier" value={editComp.multiplier} mode="decimal" minFractionDigits={1}
                                                 onValueChange={(e) => onEditField('multiplier', e.value)}/>
                                    <label htmlFor="multiplier">{t('product_detail.multiplier', 'Çarpan Katsayısı')}</label>
                                </span>
                            </div>
                            <small style={{color: '#6b7280', display: 'block', marginTop: 8}}>
                                {t('onboarding.sites.multiplierHint',
                                   'Çarpan bu sitedeki tüm satıcılar için geçerlidir. Satıcı ya da ürün URL’i bazında girilen çarpan bunu ezer.')}
                            </small>
                        </div>

                        {/* Daily request budget -- admin-only, lives on campaign.settings.site_budgets
                            keyed by normalized host, not on this row's own document. Two monitored
                            rows can normalize to the same host and therefore share one entry. */}
                        {isAdmin() && (
                            <div>
                                <div style={{fontWeight: 700, marginBottom: 4}}>{t('onboarding.sites.budgetSection', 'Daily request budget')}</div>
                                <small style={{color: '#6b7280', display: 'block', marginBottom: 12}}>
                                    {t('onboarding.sites.budgetAppliesTo', 'Applies to: {{host}}', {host: normalizeHost(editComp.url)})}
                                </small>
                                <div style={{display: 'flex', gap: 16, flexWrap: 'wrap'}}>
                                    <span style={{flex: 1, minWidth: 200}}>
                                        <span className="p-float-label">
                                            <InputNumber id="search_budget" value={budgetDraft.search ?? null} min={0} showButtons={false}
                                                         placeholder={t('suggestion.site_budget_no_cap')}
                                                         onValueChange={(e) => setBudgetDraft(b => ({...b, search: e.value}))}/>
                                            <label htmlFor="search_budget">{t('suggestion.search_budget')}</label>
                                        </span>
                                        <div className="p-text-secondary" style={{fontSize: '0.8em', marginTop: '4px'}}>
                                            {budgetTotalNote('search')}
                                        </div>
                                    </span>
                                    <span style={{flex: 1, minWidth: 200}}>
                                        <span className="p-float-label">
                                            <InputNumber id="refresh_budget" value={budgetDraft.refresh ?? null} min={0} showButtons={false}
                                                         placeholder={t('suggestion.site_budget_no_cap')}
                                                         onValueChange={(e) => setBudgetDraft(b => ({...b, refresh: e.value}))}/>
                                            <label htmlFor="refresh_budget">{t('suggestion.refresh_budget')}</label>
                                        </span>
                                        <div className="p-text-secondary" style={{fontSize: '0.8em', marginTop: '4px'}}>
                                            {budgetTotalNote('refresh')}
                                        </div>
                                    </span>
                                </div>
                            </div>
                        )}

                        {isManufacturer && (
                            <div>
                                <div style={{fontWeight: 700, marginBottom: 12}}>{t('onboarding.sites.violationSection', 'İhlal & İletişim')}</div>
                                <div style={{display: 'flex', gap: 12, marginBottom: 18, flexWrap: 'wrap'}}>
                                    <ToggleButton onLabel={t('campaign.competitors.authorized', 'Yetkili')} offLabel={t('campaign.competitors.not_authorized', 'Yetkisiz')}
                                                  onIcon="pi pi-check" offIcon="pi pi-times" checked={!!editComp.is_authorized}
                                                  onChange={(e) => onEditField('is_authorized', e.value)}/>
                                    <ToggleButton onLabel={t('campaign.competitors.notified', 'Bildirildi')} offLabel={t('campaign.competitors.not_notified', 'Bildirilmedi')}
                                                  onIcon="pi pi-check" offIcon="pi pi-times" checked={!!editComp.is_notified}
                                                  onChange={(e) => onEditField('is_notified', e.value)}/>
                                </div>
                                <div style={{display: 'flex', flexDirection: 'column', gap: 18}}>
                                    <span className="p-float-label">
                                        <InputText id="email" value={editComp.email || ''} tooltip={t('actions.multiple_email_format')}
                                                   onChange={(e) => onEditField('email', e.target.value)}/>
                                        <label htmlFor="email">{t('campaign.competitors.email', 'E-posta')}</label>
                                    </span>
                                    <span className="p-float-label">
                                        <InputText id="phone" value={editComp.phone || ''} onChange={(e) => onEditField('phone', e.target.value)}/>
                                        <label htmlFor="phone">{t('campaign.competitors.phone', 'Telefon')}</label>
                                    </span>
                                    <span className="p-float-label">
                                        <InputText id="address" value={editComp.address || ''} onChange={(e) => onEditField('address', e.target.value)}/>
                                        <label htmlFor="address">{t('campaign.competitors.address', 'Adres')}</label>
                                    </span>
                                    <span className="p-float-label">
                                        <InputTextarea id="note" rows={4} value={editComp.note || ''} onChange={(e) => onEditField('note', e.target.value)}/>
                                        <label htmlFor="note">{t('campaign.competitors.note', 'Not')}</label>
                                    </span>
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </Dialog>

            {/* copy monitored competitor URLs to another campaign */}
            <Dialog header={t('onboarding.sites.copyToCampaign', 'Başka kampanyaya kopyala')} visible={showCopyDialog}
                    style={{width: '480px', maxWidth: '95vw'}} modal onHide={() => !copying && setShowCopyDialog(false)}
                    footer={
                        <div>
                            <Button label={t('actions.cancel', 'İptal')} className="p-button-text" disabled={copying}
                                    onClick={() => setShowCopyDialog(false)}/>
                            <Button label={t('actions.copy', 'Kopyala')} icon="pi pi-copy" loading={copying}
                                    disabled={!copyTo} onClick={copyToCampaign}/>
                        </div>}>
                <p style={{marginTop: 0, color: '#6b7280'}}>{t('onboarding.sites.copyToCampaignHint', 'İzlenen rakip URL\'lerini başka bir kampanyaya kopyala.')}</p>
                <Dropdown value={copyTo} options={(campaigns || []).filter(c => c.id !== campaignId)
                              .sort((a, b) => (a.name || '').toLowerCase().localeCompare((b.name || '').toLowerCase()))}
                          optionLabel="name" optionValue="id" filter style={{width: '100%'}}
                          placeholder={t('campaign.other_campaigns', 'Hedef kampanya')}
                          onChange={(e) => setCopyTo(e.value)}/>
            </Dialog>

            {/* site definition (spider/xpath) add or edit for a host */}
            {siteDef &&
                <Dialog header={siteDef.id ? t('site_definitions.update', 'Site Tanımını Güncelle') : t('site_definitions.add', 'Yeni Site Tanımı')}
                        visible style={{width: '70vw', maxWidth: '95vw'}} modal onHide={() => setSiteDef(null)}>
                    <SiteDefinitionAdd selectedDefinition={siteDef}
                                       onComplete={(isCancel) => { setSiteDef(null); if (!isCancel) reload(); }}/>
                </Dialog>}

            {/* host-scoped admin operations (scrape + critical cleanup) */}
            {hostOpsRow &&
                <HostOpsDialog t={t} row={hostOpsRow} campaignId={campaignId} toast={toast} onError={fail}
                               supplierModuleActive={!!selectedCampaign?.settings?.supplier_module?.is_active}
                               onClose={() => setHostOpsRow(null)}/>}

            {/* marketplace sellers drill-down */}
            {sellersComp &&
                <SellersDialog t={t} competitor={sellersComp} campaignId={campaignId} isManufacturer={isManufacturer}
                               autoActiveDefault={autoActiveDefault} toast={toast} onError={fail}
                               onClose={() => setSellersComp(null)}/>}

            {/* sites in two tabs: monitored / discovered */}
            <TabView style={{marginTop: 20}}>{[
                <TabPanel key="monitored" header={`${t('onboarding.sites.monitored', 'İzlenen siteler')} (${loadingSites ? '…' : sites.length})`}>
                    {(admin || sites.length > 0) && (
                        <div style={ACTION_GRID}>
                            {admin &&
                                <ActionCard icon="pi pi-cog" color="#7c3aed" title={t('onboarding.sites.findBaseSpiders', 'Base Spider Bul')}
                                            hint={t('onboarding.sites.findBaseSpidersHint')}>
                                    <Button label={t('onboarding.sites.findBaseSpiders', 'Base Spider Bul')} icon="pi pi-cog"
                                            className="p-button-outlined" onClick={() => setShowBaseSpiderDialog(true)}/>
                                </ActionCard>}
                            {admin &&
                                <ActionCard icon="pi pi-copy" color="#0d9488" title={t('onboarding.sites.copyToCampaign', 'Başka kampanyaya kopyala')}
                                            hint={t('onboarding.sites.copyToCampaignHint', 'İzlenen rakip URL\'lerini başka bir kampanyaya kopyala.')}>
                                    <Button label={t('onboarding.sites.copyToCampaign', 'Başka kampanyaya kopyala')} icon="pi pi-copy"
                                            className="p-button-outlined" onClick={() => setShowCopyDialog(true)}/>
                                </ActionCard>}
                            {admin && sites.length > 0 &&
                                <ActionCard icon="pi pi-clipboard" color="#2563eb" title={t('onboarding.sites.copyUrls', 'URL\'leri kopyala')}
                                            hint={t('onboarding.sites.copyUrlsHint', 'İzlenen sitelerin URL\'lerini panoya kopyala.')}>
                                    <CopyToClipboard isButton={true} buttonLabel={t('onboarding.sites.copyUrls', 'URL\'leri kopyala')} textToCopy={monitoredUrlsText}/>
                                </ActionCard>}
                        </div>
                    )}

                    {/* add own site */}
                    <div style={{display: 'flex', gap: 12, alignItems: 'flex-start', flexWrap: 'wrap', marginBottom: 14}}>
                        <span className="p-float-label" style={{flex: 1, minWidth: 260}}>
                            <InputText id="siteUrl" value={url} style={{width: '100%'}} className={urlErr ? 'p-invalid' : ''}
                                       onChange={(e) => { setUrl(e.target.value); if (urlErr) setUrlErr(''); }}
                                       onKeyDown={(e) => e.key === 'Enter' && addSite()}/>
                            <label htmlFor="siteUrl">{t('onboarding.sites.urlLabel', 'İzlenecek site adresi')}</label>
                        </span>
                        <Button label={t('actions.add', 'Ekle')} icon="pi pi-plus" onClick={addSite} disabled={loading}
                                style={{width: '8em', flex: '0 0 auto'}}/>
                    </div>
                    {urlErr
                        ? <small style={{color: '#e24c4c', display: 'block', marginBottom: 8}}>{urlErr}</small>
                        : <small style={{color: '#6b7280', display: 'block', marginBottom: 8}}>
                            {t('onboarding.sites.urlHint', 'Birden fazla siteyi "||" ile ayırarak tek seferde ekleyebilirsin.')}
                        </small>}

                    {!loadingSites && sites.length > 0 &&
                        <div style={{display: 'flex', justifyContent: 'flex-end', gap: 8, marginBottom: 8, flexWrap: 'wrap'}}>
                            <span className="p-input-icon-left" style={{width: '18em'}}>
                                <i className="pi pi-search"/>
                                <InputText value={msSearch} style={{width: '100%'}}
                                           placeholder={t('onboarding.sites.searchPlaceholder', 'Search site')}
                                           onChange={(e) => { setMsSearch(e.target.value); setMsFirst(0); }}/>
                            </span>
                            {admin &&
                            <Dropdown value={msFilter} style={{width: '16em'}} onChange={(e) => { setMsFilter(e.value); setMsFirst(0); }}
                                      options={[
                                          {label: t('onboarding.sites.filterAll', 'Tümü'), value: 'all'},
                                          {label: t('onboarding.sites.ready', 'arama-hazır'), value: 'ready'},
                                          {label: t('onboarding.sites.needsSearch', 'site-içi arama yok'), value: 'needsSearch'},
                                          {label: t('onboarding.sites.needsSpider', 'spider gerekli'), value: 'needsSpider'},
                                          ...(admin ? [{label: t('onboarding.sites.hasBaseSpider', 'Base spider önerisi olanlar'), value: 'hasBaseSpider'}] : []),
                                          {label: t('onboarding.sites.inactive', 'pasif'), value: 'inactive'},
                                      ]}/>}
                        </div>}
                    {loadingSites
                        ? <div style={{textAlign: 'center', padding: '2em'}}>
                            <Loading/>
                            <div style={{marginTop: 10, color: '#6b7280'}}>{t('onboarding.sites.loadingSites', 'Siteler yükleniyor…')}</div>
                        </div>
                        : sites.length === 0
                        ? <Message severity="info" text={t('onboarding.sites.empty', 'Henüz site eklenmedi. URL gir ya da keşfet.')}/>
                        : (
                            <DataTable value={filteredSites} responsiveLayout="scroll" paginator={filteredSites.length > 10} rows={10}
                                       first={msFirst} onPage={(e) => setMsFirst(e.first)}
                                       emptyMessage={t('onboarding.sites.noMatch', 'Filtreyle eşleşen site yok.')}>
                                <Column header={t('onboarding.sites.site', 'Site')}
                                        body={(row) =>
                                            <span style={{display: 'flex', gap: 8, alignItems: 'center'}}>
                                                <a href={row.url} target="_blank" rel="noopener noreferrer">{row.url}</a>
                                                {row.multiplier !== null && row.multiplier !== undefined &&
                                                    <Tag value={'x' + row.multiplier} severity="info"/>}
                                            </span>}/>
                                {admin &&
                                <Column header={t('onboarding.sites.status', 'Durum')} style={{width: '20em'}}
                                        body={(row) => {
                                            const r = readiness(row);
                                            const budgetEntry = siteBudgets[normalizeHost(row.url)];
                                            return (
                                                <span style={{display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center'}}>
                                                    <Tag value={r.label} severity={r.severity}/>
                                                    {row.used_base_spider &&
                                                        <Tag value={row.used_base_spider} icon="pi pi-check" severity="success"/>}
                                                    {!siteActive(row) &&
                                                        <Tag value={t('onboarding.sites.inactive', 'pasif')} icon="pi pi-pause" severity="warning"/>}
                                                    {probeWebsites.has(getHostName(row.url)) &&
                                                        <Tag value={t('onboarding.sites.probeExists', 'probe var')} icon="pi pi-bolt" severity="info"
                                                             style={{cursor: 'pointer'}}
                                                             onClick={() => onOpenSiteDefinition && onOpenSiteDefinition(getHostName(row.url))}/>}
                                                    {isHostBudgeted(budgetEntry) &&
                                                        <Tag severity="info" value={t('onboarding.sites.budgetTagLabel', 'budget {{search}}/{{refresh}}', {
                                                            search: isBudgetValueSet(budgetEntry.search) ? budgetEntry.search : t('suggestion.site_budget_no_cap'),
                                                            refresh: isBudgetValueSet(budgetEntry.refresh) ? budgetEntry.refresh : t('suggestion.site_budget_no_cap'),
                                                        })}/>}
                                                </span>
                                            );
                                        }}/>}
                                <Column style={{width: '34em'}} body={(row) =>
                                    <span style={{display: 'flex', gap: 4, alignItems: 'center', justifyContent: 'flex-end', flexWrap: 'nowrap'}}>
                                        {row.sellers?.length > 0 &&
                                            <Button label={`${t('onboarding.sites.sellers', 'Satıcılar')} (${row.sellers.length})`}
                                                    icon="pi pi-users" className="p-button-text p-button-sm p-button-info"
                                                    tooltip={t('onboarding.sites.sellersHint', 'Bu sitedeki satıcıları yönet (çarpan, aktiflik, iletişim).')}
                                                    tooltipOptions={{position: 'bottom'}}
                                                    onClick={() => setSellersComp(row)}/>}
                                        <Button label={t('actions.edit', 'Düzenle')} icon="pi pi-pencil" className="p-button-text p-button-sm p-button-success"
                                                tooltip={t('onboarding.sites.editHint', 'Site iletişim ve kargo bilgilerini düzenle.')}
                                                tooltipOptions={{position: 'bottom'}}
                                                onClick={() => {
                                                    const entry = siteBudgets[normalizeHost(row.url)] || {};
                                                    setBudgetDraft({search: entry.search, refresh: entry.refresh});
                                                    setEditComp({...row});
                                                }}/>
                                        {/* Aktif/Pasif yap — kampanya ekleme sihirbazında gizli. */}
                                        {!wizardMode &&
                                        <Button label={siteActive(row) ? t('onboarding.sites.deactivate', 'Pasif yap') : t('onboarding.sites.activate', 'Aktif yap')}
                                                icon={siteActive(row) ? 'pi pi-pause' : 'pi pi-play'}
                                                className={'p-button-text p-button-sm ' + (siteActive(row) ? 'p-button-warning' : 'p-button-success')}
                                                tooltipOptions={{position: 'bottom'}}
                                                onClick={() => toggleActive(row)}/>}
                                        <Button label={t('actions.remove', 'Kaldır')} icon="pi pi-trash" className="p-button-text p-button-danger p-button-sm"
                                                tooltipOptions={{position: 'bottom'}}
                                                onClick={() => removeSite(row)}/>
                                        {admin &&
                                            <Button icon="pi pi-ellipsis-v" className="p-button-text p-button-sm p-button-secondary"
                                                    tooltip={t('actions.more', 'Diğer')} tooltipOptions={{position: 'bottom'}}
                                                    onClick={(e) => { setMenuRow(row); rowMenu.current.toggle(e); }}/>}
                                    </span>}/>
                            </DataTable>
                        )}

                    {/* per-row overflow actions: probe / site definition / delete */}
                    <OverlayPanel ref={rowMenu} style={{width: '16em'}}>
                        {menuRow &&
                            <div style={{display: 'flex', flexDirection: 'column', gap: 2}}>
                                {admin &&
                                    <ProbeLauncher siteUrl={menuRow.url} productUrl={menuRow.sample_product_url} campaignId={campaignId}
                                                   onStarted={() => { rowMenu.current.hide(); reload(); }}
                                                   buttonClassName="p-button-text p-button-sm"
                                                   buttonLabel={t('onboarding.sites.startProbe', 'Request Probe başlat')}
                                                   buttonTooltip={t('onboarding.sites.startProbeHint', 'Bu site/ürün için tarama (probe) denemesi başlat.')}/>}
                                {admin &&
                                    <Button label={t('onboarding.sites.openSiteDefinition', 'Site Tanımı')} icon="pi pi-cog"
                                            className="p-button-text p-button-sm" style={{justifyContent: 'flex-start', width: '100%'}}
                                            tooltip={t('onboarding.sites.openSiteDefinitionHint', 'Bu host\'un spider/xpath tanımını aç (tanımlıysa düzenle, değilse ekle).')}
                                            tooltipOptions={{position: 'left'}}
                                            onClick={() => openSiteDefinition(menuRow)}/>}
                                {admin &&
                                    <Button label={t('onboarding.sites.hostOps.title', 'Host işlemleri')} icon="pi pi-server"
                                            className="p-button-text p-button-sm" style={{justifyContent: 'flex-start', width: '100%'}}
                                            tooltip={t('onboarding.sites.hostOps.hint', 'Bu site için tarama ve kritik temizlik işlemleri.')}
                                            tooltipOptions={{position: 'left'}}
                                            onClick={() => { rowMenu.current.hide(); setHostOpsRow(menuRow); }}/>}
                            </div>}
                    </OverlayPanel>
                </TabPanel>,

                admin && <TabPanel key="discovered" header={`${t('onboarding.sites.competitors')} (${candidates.length})`}>
                    <div style={ACTION_GRID}>
                        <ActionCard icon="pi pi-search" color="#d97706" title={t('onboarding.sites.discoverCompetitors', 'Rakip Keşfi')}
                                    hint={t('onboarding.sites.discoverCompetitorsHint')}>
                            {scrapingRequest && (
                                <ScrapeStatus scraping_request={scrapingRequest} campaign_id={campaignId}
                                              product_id={null} competitor_url={null} newRequest={null} newRequestCompleted={null}/>
                            )}
                            <Button label={t('onboarding.sites.discoverCompetitors', 'Rakip Keşfi')} icon="pi pi-search"
                                    className="p-button-warning" onClick={() => setShowDiscoverDialog(true)}/>
                        </ActionCard>
                        {admin && candidates.length > 0 &&
                            <ActionCard icon="pi pi-clipboard" color="#2563eb" title={t('onboarding.sites.copyUrls', 'URL\'leri kopyala')}
                                        hint={t('onboarding.sites.copyCandidatesHint', 'Keşfedilen rakip sitelerin URL\'lerini panoya kopyala.')}>
                                <CopyToClipboard isButton={true} buttonLabel={t('onboarding.sites.copyUrls', 'URL\'leri kopyala')} textToCopy={candidateUrlsText}/>
                            </ActionCard>}
                    </div>
                    <CompetitorCandidates campaignId={campaignId} onChanged={after} onLoaded={setCandidates}
                                          onOpenSiteDefinition={onOpenSiteDefinition} probeWebsites={probeWebsites}/>
                </TabPanel>,

                admin && <TabPanel key="newsellers" header={`${t('onboarding.sites.newSellers', 'Yeni Satıcılar')} (${newSellers.length})`}>
                    <div style={ACTION_GRID}>
                        <ActionCard icon="pi pi-search-plus" color="#0891b2" title={t('onboarding.sites.findNewSellers', 'Yeni Satıcı Bul')}
                                    hint={t('onboarding.sites.findNewSellersHint')}>
                            <Button label={t('onboarding.sites.findNewSellers', 'Yeni Satıcı Bul')} icon="pi pi-search-plus"
                                    onClick={() => setShowNewSellersDialog(true)}/>
                        </ActionCard>
                        {admin && newSellers.length > 0 &&
                            <ActionCard icon="pi pi-clipboard" color="#2563eb" title={t('onboarding.sites.copyUrls', 'URL\'leri kopyala')}
                                        hint={t('onboarding.sites.copyNewSellersHint', 'Yeni satıcı öneri URL\'lerini panoya kopyala.')}>
                                <CopyToClipboard isButton={true} buttonLabel={t('onboarding.sites.copyUrls', 'URL\'leri kopyala')}
                                                 textToCopy={newSellers.map(s => s.url).join('\n')}/>
                            </ActionCard>}
                    </div>
                    {admin && newSellers.length > 0 &&
                        <div style={{display: 'flex', justifyContent: 'flex-end', gap: 8, marginBottom: 8, flexWrap: 'wrap'}}>
                            <span className="p-input-icon-left" style={{width: '18em'}}>
                                <i className="pi pi-search"/>
                                <InputText value={nsSearch} style={{width: '100%'}}
                                           placeholder={t('onboarding.sites.searchPlaceholder', 'Search site')}
                                           onChange={(e) => { setNsSearch(e.target.value); setNsFirst(0); }}/>
                            </span>
                            <Dropdown value={nsFilter} style={{width: '15em'}} onChange={(e) => { setNsFilter(e.value); setNsFirst(0); }}
                                      options={[
                                          {label: t('onboarding.sites.filterAll', 'Tümü'), value: 'all'},
                                          {label: t('onboarding.sites.scrapable', 'taranabilir'), value: 'scrapable'},
                                          {label: t('onboarding.sites.needsSpider', 'spider gerekli'), value: 'needsSpider'},
                                      ]}/>
                        </div>}
                    {loadingNewSellers
                        ? <div style={{textAlign: 'center', padding: '2em'}}>
                            <Loading/>
                            <div style={{marginTop: 10, color: '#6b7280'}}>{t('onboarding.sites.loadingNewSellers', 'Yeni satıcılar yükleniyor…')}</div>
                        </div>
                        : newSellers.length === 0
                        ? <Message severity="info" text={t('onboarding.sites.newSellersEmpty', 'Yeni satıcı önerisi yok.')}/>
                        : (
                            <DataTable value={filteredNewSellers} responsiveLayout="scroll" paginator={filteredNewSellers.length > 10} rows={10} dataKey="url"
                                       first={nsFirst} onPage={(e) => setNsFirst(e.first)}
                                       emptyMessage={t('onboarding.sites.noMatch', 'Filtreyle eşleşen site yok.')}>
                                <Column header={t('onboarding.sites.site', 'Site')}
                                        body={(row) => <a href={row.url} target="_blank" rel="noopener noreferrer">{row.url}</a>}/>
                                {admin &&
                                    <Column header={t('onboarding.sites.status', 'Durum')} style={{width: '12em'}}
                                            body={(row) => (row.is_spider_ready || row.spider_name)
                                                ? <Tag value={row.spider_name || t('onboarding.sites.scrapable', 'taranabilir')} icon="pi pi-check" severity="success"/>
                                                : <Tag value={t('onboarding.sites.needsSpider', 'spider gerekli')} icon="pi pi-exclamation-triangle" severity="warning"/>}/>}
                                <Column style={{width: '12em', textAlign: 'center'}} body={(row) =>
                                    <span style={{display: 'flex', gap: 6, justifyContent: 'center'}}>
                                        <Button label={t('actions.add', 'Ekle')} icon="pi pi-plus" className="p-button-sm"
                                                onClick={() => addSuggestionToSites(row)}/>
                                        <Button icon="pi pi-trash" className="p-button-text p-button-danger p-button-sm"
                                                tooltip={t('actions.remove', 'Kaldır')} onClick={() => deleteSuggestion(row)}/>
                                    </span>}/>
                            </DataTable>
                        )}
                </TabPanel>,
            ].filter(Boolean)}</TabView>

        </div>
    );
};

export default SitesOnboardingStep;
